'use client';

import React from 'react';
import { Modal } from '@/components/ui/Modal';
import { IconCheckCircle, IconAlertCircle } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { getRodentTitleAndDescription } from '@/app/building/context/constants';

interface RodentHistoryEntry {
  inspectionDate: string;
  approvedDate?: string;
  result?: string;
  inspectionType?: string;
}

interface RodentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  rodentData: RodentHistoryEntry[];
  isBlurRequired?: boolean;
}

interface RodentHistoryContentProps {
  rodentData: RodentHistoryEntry[];
  isBlurRequired?: boolean;
}

// Format date to "MMM DD, YYYY" format
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

// Content component for use with ModalTrigger
export function RodentHistoryContent({
  rodentData,
  isBlurRequired = false,
}: RodentHistoryContentProps) {
  const entries = Array.isArray(rodentData) ? rodentData : [];

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body-2 text-secondary">No rodent history data available.</p>
      </div>
    );
  }

  return (
    <div>
      {/* History Entries */}
      {entries.map((entry, index, array) => {
        const result = entry.result || '';
        const formattedInspectionDate = formatDate(entry.inspectionDate);

        // Get title and description using shared logic
        const { title, description } = getRodentTitleAndDescription(result);

        // Determine variant based on result
        let variant: 'success' | 'alert' | 'caution' = 'success';
        if (result === 'Rat Activity' || result === 'Failed for Other R') {
          variant = 'alert';
        } else if (result === 'Bait applied' || result === 'Monitoring visit') {
          variant = 'caution';
        } else if (result === 'Passed') {
          variant = 'success';
        }

        const iconColor =
          variant === 'success'
            ? 'text-success'
            : variant === 'alert'
              ? 'text-alert'
              : 'text-caution';
        const iconBg =
          variant === 'success'
            ? 'bg-success-low'
            : variant === 'alert'
              ? 'bg-alert-low'
              : 'bg-caution-low';

        return (
          <div key={index}>
            <div className="flex items-center gap-3 px-2 py-3">
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center shrink-0',
                  iconBg,
                )}
              >
                {variant === 'success' ? (
                  <IconCheckCircle className={cn('w-6 h-6', iconColor)} />
                ) : (
                  <IconAlertCircle className={cn('w-6 h-6', iconColor)} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={cn('text-body-2 text-primary')}>{title}</span>
                  <span className="text-body-2 text-primary shrink-0">
                    {formattedInspectionDate}
                  </span>
                </div>
                <p className="text-caption text-secondary">{description}</p>
              </div>
            </div>
            {index < array.length - 1 && <div className="border-b border-tertiary mx-2"></div>}
          </div>
        );
      })}
    </div>
  );
}
